import React, { useEffect, useState } from 'react';
import Icon from '@components/atoms/Icon';

interface CartHoldTimerProps {
  holdMessage: string; // Message with [time] placeholder
  holdEndTime: string; // End time in ISO 8601 format
  onRemainingTimeUpdate?: (isActive: boolean) => void; // Callback to notify the parent about timer status
  isCartPage: boolean;
}

const CartHoldTimer: React.FC<CartHoldTimerProps> = ({ holdMessage, holdEndTime, onRemainingTimeUpdate, isCartPage }) => {
  const [remainingTime, setRemainingTime] = useState<string | null>(null);

  useEffect(() => {
    const calculateRemainingTime = () => {
      const endTime = new Date(holdEndTime).getTime();
      const currentTime = new Date().getTime();

      if (endTime > currentTime) {
        const timeDiff = Math.max(endTime - currentTime, 0); // Ensure non-negative time

        const totalSeconds = Math.floor(timeDiff / 1000);
        const days = Math.floor(totalSeconds / (60 * 60 * 24));
        const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
        const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
        const seconds = totalSeconds % 60;

        let timeString;
        if (totalSeconds < 3600) {
          // Less than 60 minutes -> show mm:ss
          timeString = `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
        } else if (totalSeconds >= 86400) {
          // More than 24 hours -> show Xdays Yhours Z:YY minutes
          timeString = `${days}d ${hours}h ${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
        } else {
          // Between 60 minutes and 24 hours -> show Yhours Z:YY minutes
          timeString = `${hours}h ${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
        }

        setRemainingTime(timeString);
        onRemainingTimeUpdate?.(true); // Notify parent that timer is active
      } else {
        setRemainingTime(null); // Timer expired
        onRemainingTimeUpdate?.(false); // Notify parent that timer has expired
      }
    };

    calculateRemainingTime(); // Initial calculation
    const interval = setInterval(calculateRemainingTime, 1000); // Update every second

    return () => clearInterval(interval); // Cleanup interval on unmount
  }, [holdEndTime, onRemainingTimeUpdate]);

  if (!remainingTime) {
    return null; // Hide component if timer has expired
  }

  // Replace [time] placeholder dynamically in the cart hold message
  const dynamicMessage = holdMessage.replace('[time]', remainingTime);

  return (
    <div className={`flex items-center justify-center gap-4 bg-yellow-1 py-2 px-4 rounded-sm mb-8 ${isCartPage ? '' : 'mx-2.5'}`}>
      <Icon name="clock" className="text-black w-5 h-5" />
      <div
        className="body-xxs font-extrabold"
        dangerouslySetInnerHTML={{ __html: dynamicMessage }}
      />
    </div>
  );
};

export default CartHoldTimer;
